import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const experienceData = [
  {
    id: 1,
    role: "Founder & CEO",
    company: "FIRST Group",
    location: "Bali, Indonesia",
    period: "2024 – Present",
    description: "Technology-driven real estate and operational infrastructure group serving international investors.",
    highlights: [
      "Digital operational infrastructure across multiple companies",
      "Integration of CRM, POS, booking platforms and automation systems",
      "Scalable technology workflows for operations and property management",
    ],
    link: "https://first.co.id",
    current: true,
  },
  {
    id: 2,
    role: "Founder & CEO",
    company: "Mac Expert",
    location: "France / Dubai",
    period: "2016 – 2023",
    description: "Apple infrastructure consulting and advanced technical services.",
    highlights: [
      "macOS infrastructure architecture and enterprise deployment",
      "Cybersecurity vulnerability detection and penetration testing",
      "Advanced diagnostics, system recovery and hardware engineering",
    ],
    links: [
      { url: "https://macexpert.fr", label: "MacExpert.FR" },
      { url: "https://macexpert.ae", label: "MacExpert.AE" },
    ],
    current: false,
  },
  {
    id: 3,
    role: "Chief Operating Officer",
    company: "Koosmik",
    location: "Luxembourg",
    period: "2021 – 2022",
    description: "Fintech company focused on digital payments infrastructure.",
    highlights: [
      "Coordination of product, engineering and operational teams",
      "Deployment of digital financial infrastructure",
      "Strategic partnerships and international operations",
    ],
    current: false,
  },
  {
    id: 4,
    role: "Africa Area Manager",
    company: "Apple CEMEA",
    location: "CEMEA Region",
    period: "2005 – 2016",
    description: "Regional leadership role overseeing Apple ecosystem expansion in CEMEA region.",
    highlights: [
      "Development of Apple reseller and distribution networks",
      "Multi-country operational management",
      "Technology ecosystem deployment across regional markets",
    ],
    current: false,
  },
];

export const Experience = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const timelineRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.from(headerRef.current, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      // Timeline line animation
      gsap.from(timelineRef.current, {
        scaleY: 0,
        duration: 1.5,
        ease: "power2.out",
        transformOrigin: "top",
        scrollTrigger: {
          trigger: timelineRef.current,
          start: "top 70%",
          toggleActions: "play none none reverse"
        }
      });

      // Items stagger animation
      itemsRef.current.forEach((item, index) => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        });

        // Dot animation
        const dot = item.querySelector('.timeline-dot');
        tl.from(dot, {
          scale: 0,
          duration: 0.4,
          ease: "back.out(2)"
        });

        // Content slide in
        const content = item.querySelector('.timeline-content');
        tl.from(content, {
          x: -50,
          opacity: 0,
          duration: 0.6,
          ease: "power3.out"
        }, "-=0.2");

        // Highlights stagger
        const highlights = item.querySelectorAll('.highlight-item');
        tl.from(highlights, {
          x: -20,
          opacity: 0,
          duration: 0.4,
          stagger: 0.1,
          ease: "power2.out"
        }, "-=0.3");
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="experience"
      data-testid="experience-section"
      className="py-24 md:py-32 bg-background border-t border-border/50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="mb-16 md:mb-20">
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium block mb-4">
            02
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground">
            Experience
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div 
            ref={timelineRef}
            className="absolute left-0 md:left-8 top-0 bottom-0 w-px bg-border/50 hidden md:block" 
          />

          <div className="space-y-12">
            {experienceData.map((exp, index) => (
              <div
                key={exp.id}
                ref={(el) => itemsRef.current[index] = el}
                data-testid={`experience-item-${index}`}
                className="relative pl-0 md:pl-20"
              >
                {/* Timeline Dot */}
                <div className={`timeline-dot absolute left-0 md:left-6 top-2 w-5 h-5 rounded-full border-2 hidden md:flex items-center justify-center ${
                  exp.current 
                    ? 'border-foreground bg-foreground' 
                    : 'border-border bg-background'
                }`}>
                  {exp.current && <div className="w-2 h-2 rounded-full bg-background" />}
                </div>

                {/* Content */}
                <div className="timeline-content group">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
                    <div>
                      <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                        {exp.role}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {exp.link ? (
                          <a
                            href={exp.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-steel hover:text-foreground transition-colors flex items-center gap-1"
                          >
                            {exp.company}
                            <ArrowUpRight className="w-4 h-4" />
                          </a>
                        ) : exp.links ? (
                          <>
                            <span className="text-steel">{exp.company}</span>
                            <span className="text-muted-foreground">—</span>
                            {exp.links.map((link, idx) => (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-steel hover:text-foreground transition-colors flex items-center gap-1 text-sm"
                              >
                                {link.label}
                                <ArrowUpRight className="w-3 h-3" />
                              </a>
                            ))}
                          </>
                        ) : (
                          <span className="text-steel">{exp.company}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {exp.period}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {exp.location}
                      </span>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4">
                    {exp.description}
                  </p>

                  <ul className="space-y-2">
                    {exp.highlights.map((highlight, idx) => (
                      <li key={idx} className="highlight-item flex items-start gap-3 text-sm text-muted-foreground">
                        <span className="w-1.5 h-1.5 bg-steel rounded-full mt-2 flex-shrink-0" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
